import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Mail } from 'lucide-react';
import { api, User } from '../../lib/api';

export function UserListPage() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ data: User[] }>('/api/users'),
  });

  const inviteMutation = useMutation({
    mutationFn: () => api.post('/api/users/invite', { email: inviteEmail, role: inviteRole }),
    onSuccess: () => {
      setShowInviteModal(false);
      setInviteEmail('');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const roleLabels: Record<string, string> = { admin: '管理者', tax_advisor: '税理士', accountant: '経理担当', user: 'ユーザー', viewer: '閲覧者' };
  const statusLabels: Record<string, string> = { active: '有効', pending: '招待中', disabled: '無効' };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>ユーザー管理</h1>
        <button onClick={() => setShowInviteModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium" style={{ backgroundColor: 'var(--accent-color)' }}><UserPlus size={18} />ユーザー招待</button>
      </div>
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>
        ) : (
          <table>
            <thead><tr>
              <th style={{ color: 'var(--text-primary)' }}>名前</th>
              <th style={{ color: 'var(--text-primary)' }}>メール</th>
              <th style={{ color: 'var(--text-primary)' }}>ロール</th>
              <th style={{ color: 'var(--text-primary)' }}>状態</th>
              <th style={{ color: 'var(--text-primary)' }}>最終ログイン</th>
            </tr></thead>
            <tbody>
              {data?.data.map((user) => (
                <tr key={user.id}>
                  <td className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm" style={{ backgroundColor: 'var(--accent-color)' }}>{user.name.charAt(0)}</div>
                    <span style={{ color: 'var(--text-primary)' }}>{user.name}</span>
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{user.email}</td>
                  <td><span className="badge badge-info">{roleLabels[user.role] || user.role}</span></td>
                  <td><span className={`badge ${user.status === 'active' ? 'badge-success' : user.status === 'pending' ? 'badge-warning' : 'badge-error'}`}>{statusLabels[user.status] || user.status}</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('ja-JP') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md animate-slideIn">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>ユーザー招待</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>メールアドレス</label>
                <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>ロール</label>
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}>
                  <option value="user">ユーザー</option>
                  <option value="accountant">経理担当</option>
                  <option value="tax_advisor">税理士</option>
                  <option value="admin">管理者</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowInviteModal(false)} className="px-4 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>キャンセル</button>
              <button onClick={() => inviteMutation.mutate()} disabled={inviteMutation.isPending || !inviteEmail} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm disabled:opacity-50" style={{ backgroundColor: 'var(--accent-color)' }}><Mail size={16} />招待メール送信</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

